/**
 * /admin/players/bulk-upload — N6 — CSV player import.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Upload, AlertTriangle, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { bulkUploadPlayers } from "../../api/admin.extras.api";

export default function AdminPlayerBulkUpload() {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!file) { setError("Pick a CSV file."); return; }
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await bulkUploadPlayers(file, dryRun);
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Upload failed.");
    } finally { setBusy(false); }
  }

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      {/* 2026-06-08 — Back affordance per nav audit. */}
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
        aria-label="Back to admin"
      >
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>
      <header>
        <h1 className="text-2xl font-semibold">Bulk upload players</h1>
        <p className="text-sm text-text-secondary mt-1">
          Idempotent — dedup by (first_name, last_name, nationality). Existing
          rows are skipped with a warning.
        </p>
      </header>

      <form onSubmit={submit} className="space-y-4 rounded-md border border-border-default bg-bg-primary p-5">
        <label className="block">
          <span className="text-sm font-medium">CSV file</span>
          <input
            type="file" accept=".csv" required
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            className="mt-1 block w-full text-sm"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
          <span>Dry run (count rows, don't write)</span>
        </label>
        <div>
          <button type="submit" disabled={busy} className="px-3 py-2 text-sm rounded-md bg-button-primary text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {dryRun ? "Scan + preview" : "Upload + create"}
          </button>
        </div>
      </form>

      {error && (
        <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">
          <AlertTriangle className="w-4 h-4 inline-block mr-1.5" />{error}
        </div>
      )}

      {result && (
        <div className="rounded-md border border-border-default bg-bg-primary p-5 space-y-2 text-sm">
          <div className="flex items-center gap-2 font-medium">
            <CheckCircle2 className="w-4 h-4 text-green-700" />
            {result.dry_run ? "Dry run result" : "Created"}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label={result.dry_run ? "Would create" : "Created"} value={result.created} color="text-green-700" />
            <Stat label="Skipped (duplicate)" value={result.skipped_existing} />
            <Stat label="Rejected (bad row)" value={result.skipped_bad} color="text-red-600" />
          </div>
          {result.errors?.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-sm font-medium">Show errors ({result.errors.length}{result.more_errors ? `+${result.more_errors}` : ""})</summary>
              <ul className="mt-2 text-xs space-y-0.5 font-mono">
                {result.errors.map((e, i) => <li key={i} className="text-red-600">{e}</li>)}
                {result.more_errors > 0 && <li className="text-text-secondary">… and {result.more_errors} more.</li>}
              </ul>
            </details>
          )}
        </div>
      )}

      <details className="text-sm text-text-secondary">
        <summary className="cursor-pointer">Expected CSV format</summary>
        <pre className="mt-2 p-3 bg-bg-secondary rounded text-xs overflow-x-auto">{`first_name,last_name,nick_name,nationality_code,gender,role,batting_hand,bowling_arm,bowling_style
Virat,Kohli,Cheeku,IN,MALE,BATTER,RIGHT,RIGHT,MEDIUM
Rachin,Ravindra,,NZ,MALE,ALL_ROUNDER,LEFT,LEFT,ORTHODOX
Smriti,Mandhana,Smriti,IN,FEMALE,BATTER,LEFT,RIGHT,OFF_BREAK`}</pre>
        <p className="mt-2 text-xs">Required: first_name, nationality_code (2-letter ISO). All others optional.</p>
      </details>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded border border-border-default px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-text-secondary">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${color || ""}`}>{value ?? 0}</div>
    </div>
  );
}
